import fs from 'fs'
import path from 'path'
import chalk from 'chalk'
import moment from 'moment'
import {getDMMF} from '@prisma/sdk'
import {camelCase} from "lodash/string";
import getDataSchema from "../../../utils/getDataSchema";

export default async function (name) {
    name = name.charAt(0).toUpperCase() + camelCase(name).slice(1);
    const schema = await getDataSchema();
    const datamodel = await getDMMF({datamodel: schema});

    let target;
    if (target = datamodel.datamodel.models.find(m => m.name === name)) {
        const p = path.join(process.cwd(), 'api', 'src', 'migrations', `${moment().format('YYYYMMDDHHmmss')}_create_table_${name}.js`);
        console.log(chalk.yellow(`Generating ${name} migration to ${p}`));
        const {fields, relations, relationTables} = generateFields(target, datamodel.datamodel);
        const m = template(name, fields, relations, relationTables);
        fs.writeFileSync(p, m)
    } else if (target = datamodel.datamodel.enums.find(m => m.name === name)) {
        throw new Error(`${name} is a enum. Enums do not require a migration`);
    } else {
        throw new Error('Model not found');
    }

}

function template(table, fields, relations: string[] = [], relationTables: string) {
    return `exports.up = async function(knex) {
    await knex.schema.createTable('${table}', function (table) {
${fields}
    });
${relationTables ? relationTables + "\n" : ''}    return true;
};

exports.down = async function(knex) {
${relations.map(r => `    await knex.schema.dropTableIfExists('_${r}');`).join("\n")}${relations.length ? "\n" : ''}    await knex.schema.dropTableIfExists('${table}');
    return true;
};`
}

function generateFields(model: Array, datamodel) {
    const typeMap = (f, name) => {
        name = name || f.name;
        if (f.kind === 'enum') {
            return `string('${name}', 191)`;
        } else if (f.isList) {
            return `json('${name}')`;
        }
        switch (f.type) {
            case 'Int':
                return f.default?.name === 'autoincrement' ? `increments('${name}')` : `integer('${name}')`;
            case 'BigInt':
                return f.default?.name === 'autoincrement' ? `bigIncrements('${name}')` : `bigInteger('${name}')`;
            case 'Decimal':
            case 'Float':
                return `decimal('${name}', 65, 30)`;
            case 'String':
                return `string('${name}', 191)`;
            case 'Boolean':
                return `boolean('${name}')`;
            case 'Bytes':
                return `binary('${name}')`;
            case 'Json':
                return `json('${name}')`;
            case 'DateTime':
                return `datetime('${name}')`;
        }
    }

    const getDefault = (f) => {
        if (!f.default) return null;
        if (typeof f.default === "object") {
            switch (f.default.name) {
                case 'now':
                    return 'knex.fn.now()';
            }
            return null;
        }
        return typeof f.default === 'string' ? `"${f.default}"` : f.default;
    }
    let fields = [];
    let relations = [];
    let relationTables = [];
    if (!model.fields.find(f => f.isId)) {
        fields.push(`      table.string('id', 25);\n      table.primary('id');`)
    }
    fields = fields.concat(model.fields.filter(f => f.kind !== 'object').map(f => {
        let str = `      table.${typeMap(f)}`;
        if (!f.isRequired) str += '.nullable()';
        if (getDefault(f)) str += `.defaultTo(${getDefault(f)})`;
        if (model.fields.find(ff => ff.relationFromFields?.includes(f.name)) && ['Int', 'BigInt'].includes(f.type)) str += '.unsigned()'
        if (f.isId) str += `.primary()`;
        if (f.isUnique) str += `.unique()`;
        return str + ';'
    }))

    fields = fields.concat(model.fields.filter(f => f.kind === 'object').map(f => {
        const related = datamodel.models.find(m => m.name === f.type);
        if (f.relationFromFields?.length) {
            let str = `      table.foreign(${JSON.stringify(f.relationFromFields)}).references(${JSON.stringify(f.relationToFields)}).inTable("${f.type}")`;
            if (f.relationOnDelete !== 'NONE') {
                str += `.onDelete(${f.relationOnDelete})`
            }
            return str + ';'
        } else if (f.isList && related?.fields.find(ff => ff.relationName === f.relationName)?.isList) {
            const models = model.name < related.name ? [model, related] : [related, model];
            const local = typeMap(model.fields.find(ff => ff.isId), model.name < related.name ? 'A' : 'B')
            let external = typeMap(related?.fields.find(ff => ff.isId), model.name < related.name ? 'B' : 'A');
            const fields = (model.name < related.name ? [local, external] : [external, local]).map(ff => {
                if (ff.startsWith('bigIncrements')) {
                    ff = ff.replace('bigIncrements', 'bigInteger');
                    ff += '.unsigned()'
                } else if (ff.startsWith('increments')) {
                    ff = ff.replace('increments', 'integer');
                    ff += '.unsigned()'
                }
                return ff;
            });
            const keys = models.map((mm, index) => {
                return `foreign('${index === 0 ? 'A' : 'B'}').references('${mm.fields.find(ff => ff.isId).name}').inTable('${mm.name}')`
            })

            relations.push(f.relationName);
            relationTables.push(`    await knex.schema.hasTable('_${f.relationName}').then(has => !has && knex.schema.createTable('_${f.relationName}', function (table) {
${fields.map(s => `        table.${s};`).join("\n")}${keys.length ? "\n" : ''}${keys.map(s => `        table.${s};`).join("\n")}
    }));`)
        }
    }))

    return {fields: fields.join("\n"), relationTables: relationTables.join("\n"), relations};
}
